import type mixpanelSdk from 'mixpanel-browser';

type Sdk = typeof mixpanelSdk;
type Properties = Record<string, unknown>;
type Command = { event: boolean; apply: (sdk: Sdk) => void };

/** Keep analytics off the render path while retaining identity/event order. */
export function createDeferredAnalytics(loadSdk: () => Promise<Sdk>, maxQueued = 200) {
  let sdk: Sdk | null = null;
  let loading: Promise<void> | null = null;
  const queue: Command[] = [];
  const apply = (command: Command) => {
    try { command.apply(sdk!); } catch { /* Analytics cannot interrupt use. */ }
  };
  const enqueue = (command: Command) => {
    if (sdk) { apply(command); return; }
    if (queue.length >= maxQueued) {
      // Prefer discarding telemetry over identity/register/reset commands.
      const event = queue.findIndex(item => item.event);
      queue.splice(event < 0 ? 0 : event, 1);
    }
    queue.push(command);
  };
  const api = {
    track(event: string, properties?: Properties) {
      const snapshot = properties ? { ...properties } : undefined;
      enqueue({ event: true, apply: client => { client.track(event, snapshot); } });
    },
    register(properties: Properties) {
      const snapshot = { ...properties };
      enqueue({ event: false, apply: client => { client.register(snapshot); } });
    },
    identify(id: string) { enqueue({ event: false, apply: client => { client.identify(id); } }); },
    reset() { enqueue({ event: false, apply: client => { client.reset(); } }); },
    people: {
      set(properties: Properties) {
        const snapshot = { ...properties };
        enqueue({ event: false, apply: client => { client.people.set(snapshot); } });
      },
    },
  };
  return {
    api,
    load(): Promise<void> {
      if (sdk) return Promise.resolve();
      if (loading) return loading;
      loading = loadSdk().then(client => {
        sdk = client;
        for (const command of queue.splice(0)) apply(command);
      }).catch(() => { /* Retain the bounded queue for an online retry. */ }).finally(() => { loading = null; });
      return loading;
    },
  };
}
